import React from "react";
import styled from "styled-components";
import { Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { modalActionStyles } from "./modalActionStyles";

const StyledModal = styled(Modal)`
  z-index: 1060 !important;

  .modal-dialog {
    max-width: ${({ $wide }) => ($wide ? "1040px" : "520px")};
    width: calc(100% - 20px);
    margin: ${({ $topSpacing }) => $topSpacing || "1.5rem auto"};
    padding: 0;
  }

  .modal-content {
    display: flex;
    flex-direction: column;
    max-width: 100%;
    max-height: 100%;
    border-radius: 16px;
    border: 1px solid rgba(229, 224, 215, 0.8);
    box-shadow: 0 24px 70px rgba(75, 51, 53, 0.16);
    overflow: hidden;
    z-index: 1060;
  }

  .modal-body {
    padding: 1.25rem;
    font-size: 15px;
    color: #333;
    overflow: auto;
    flex: 1 1 auto;
  }

  /* Opt-in: fill the window instead of hugging the content. The body becomes a
     flex column so a child can claim the leftover space and scroll on its own,
     which keeps a header and a search box pinned while a long list moves. */
  ${({ $fullHeight }) =>
    $fullHeight &&
    `
      .modal-dialog {
        height: calc(100vh - 2rem);
        max-height: calc(100vh - 2rem);
      }

      .modal-content {
        height: 100%;
      }

      .modal-body {
        display: flex;
        flex-direction: column;
        min-height: 0;
        overflow: hidden;
      }
    `}

  .modal-header {
    position: relative;
    background-color: #ffffff;
    border: none;
    border-bottom: 1px solid #ece6de;
    display: flex;
    justify-content: space-between;
    align-items: center;
    min-height: 60px;
    padding: 1rem 1.25rem;
  }

  .modal-title {
    margin: 0;
    padding-right: 42px;
    font-weight: 700;
    font-size: 17px;
    color: var(--app-orange, #ee754c);
    letter-spacing: -0.015em;
  }

  /* A modal may put one small control beside its title - a mode switch, say -
     rather than pushing it into the body where it reads as part of the form.
     Only headers that ask for it change shape, so every other modal is
     untouched. */
  .modal-header.has-extra {
    display: grid;
    /* Title, control, and the space the close button floats in. The control
       gets the middle column, so it is centred on the modal rather than
       pushed against the close button. */
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    gap: 12px;
  }

  .modal-header.has-extra .modal-title {
    min-width: 0;
    padding-right: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .modal-header-extra {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 0;
  }

  .modal-footer {
    background-color: white;
    border: none;
    border-top: 1px solid #f1ece7;
    padding: 1rem 1.25rem;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
  }

  @media (max-width: 576px) {
    .modal-dialog {
      width: calc(100% - 12px);
      margin: 0.5rem auto;
    }

    .modal-body {
      padding: 1rem;
    }

    /* Too narrow for title and control on one line, so the control takes its
       own row instead of squeezing the title to nothing. */
    .modal-header.has-extra {
      grid-template-columns: 1fr;
      row-gap: 8px;
      padding-right: 3.25rem;
    }

    .modal-header-extra {
      justify-content: flex-start;
    }

    .modal-footer {
      padding: 0.75rem 1rem 1rem;
    }
  }

  ${modalActionStyles}
`;

const CloseButton = styled.button`
  position: absolute;
  top: 14px;
  right: 16px;
  width: 30px;
  height: 30px;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: #f5f2ef;
  color: #756a6c;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.16s ease;

  &:hover {
    background: #fff0e9;
    color: #d9603b;
  }

  svg {
    font-size: 14px;
  }
`;

export const CustomModal = ({
  show,
  onHide,
  title,
  body,
  footer,
  centered = true,
  topSpacing = null,
  fullHeight = false,
  // Opt-in: forms laid out in columns need more than the default 520px.
  wide = false,
  children,
  removeCloseButton = false,
  headerAction = null,
  // Sits between the title and the close button; keeps the close button.
  headerExtra = null,
  ...rest
}) => {
  return (
    <StyledModal
      show={show}
      onHide={onHide}
      centered={centered}
      backdrop="static"
      $topSpacing={topSpacing}
      $fullHeight={fullHeight}
      $wide={wide}
      {...rest}
    >
      {(title || onHide || headerAction || headerExtra) && (
        <div className={`modal-header${headerExtra ? " has-extra" : ""}`}>
          {title && <h5 className="modal-title">{title}</h5>}

          {headerExtra && (
            <div className="modal-header-extra">{headerExtra}</div>
          )}

          {headerAction ? (
            headerAction
          ) : removeCloseButton ? (
            <></>
          ) : onHide ? (
            <CloseButton type="button" onClick={onHide} aria-label="Close">
              <FontAwesomeIcon icon={faXmark} />
            </CloseButton>
          ) : (
            <></>
          )}
        </div>
      )}

      <div className="modal-body">{body || children}</div>

      {footer && <div className="modal-footer">{footer}</div>}
    </StyledModal>
  );
};

const StyledButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  border: none;
  border-radius: 8px;
  font-family:
    "Inter",
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    Roboto,
    "Helvetica Neue",
    Arial,
    sans-serif;
  font-size: ${({ $fontSize }) => $fontSize || "14px"};
  font-weight: 500;
  cursor: pointer;
  background-color: ${({ $color }) => $color || "#007bff"};
  color: white;
  transition:
    background-color 0.2s ease-in-out,
    box-shadow 0.2s ease-in-out;

  &:hover {
    background-color: ${({ $color }) => darkenColor($color || "#007bff", 0.1)};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: scale(0.98);
  }

  &:disabled {
    background-color: #ccc !important;
    color: #666;
    cursor: not-allowed !important;
    box-shadow: none;
  }
`;

// Helper function to darken the hover color
function darkenColor(hex, amount) {
  try {
    let col = hex;
    if (col.startsWith("#")) col = col.slice(1);

    const num = parseInt(col, 16);
    let r = (num >> 16) - amount * 255;
    let g = ((num >> 8) & 0x00ff) - amount * 255;
    let b = (num & 0x0000ff) - amount * 255;

    r = Math.max(0, r);
    g = Math.max(0, g);
    b = Math.max(0, b);

    return `rgb(${r}, ${g}, ${b})`;
  } catch {
    return hex;
  }
}

export const ModernButton = ({
  children,
  color,
  fontSize,
  onClick,
  style,
  type = "button",
  disabled = false,
}) => {
  return (
    <StyledButton
      $color={color}
      onClick={onClick}
      style={style}
      $fontSize={fontSize}
      type={type}
      disabled={disabled}
    >
      {children}
    </StyledButton>
  );
};
